import { SyncStatus } from '@seapoint/server/enums';
import axios from 'axios';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import { AccountView, CompanyView, InstitutionView } from '../types/entity-types';
import { parseYapilyError } from '../utils/parse-yapily-error';

export interface SyncResponse {
  accounts: AccountView[];
  countTransactions: number;
}

export const useOpenBankingAPI = (company: CompanyView) => {
  const searchParams = new URLSearchParams(location.search);
  const statusParam = searchParams.get('status');
  const companyIdParam = searchParams.get('companyId');

  const shouldFetchSyncStatus = statusParam === 'connected' && companyIdParam;

  const companyCountry = company.legalEntity.country;
  const companyId = company.id;

  const {
    data: institutionsData,
    isLoading: isLoadingInstitutions,
    error: institutionsError,
  } = useSWRImmutable<{
    institutions: InstitutionView[];
  }>(companyCountry ? `/api/open-banking/institutions?country=${companyCountry}` : null);
  const {
    data: authLinkData,
    trigger: getAuthLink,
    isMutating: isFetchingAuthLink,
    error: authLinkError,
  } = useSWRMutation<
    { link: string },
    Error,
    string,
    { companyId: string; institutionId: string; userName: string; psuId?: string }
  >(`/api/open-banking/auth-link`, authLinkFetcher);
  const {
    data: syncStatusData,
    isLoading: isLoadingSyncStatus,
    error: syncStatusError,
  } = useSWRImmutable<{ syncStatus: SyncStatus; syncStats: { totalAccounts: number } }, Error>(
    shouldFetchSyncStatus ? `/api/companies/${encodeURIComponent(companyId)}/sync/status` : null,
    {
      refreshInterval: (latestData) => {
        if (latestData?.syncStatus === SyncStatus.IN_PROGRESS) {
          // If the sync is in progress we keep on polling until it's complete
          return 1000;
        }

        return 0;
      },
    },
  );

  return {
    data: {
      institutionsData,
      authLinkData,
      syncStatusData,
    },
    isLoadingData: {
      isLoadingInstitutions,
      isFetchingAuthLink,
      isLoadingSyncStatus:
        isLoadingSyncStatus ||
        // Keeps the page loading until the sync data is available
        // Otherwise, sometimes the success modal would take a while to load, leaving users puzzled as to what is happening
        // Relying on isLoadingSyncStatus is not enough, because once shouldFetchSyncStatus is true, for a split second the loading is still false
        (shouldFetchSyncStatus && !syncStatusData && !syncStatusError),
    },
    errorData: { institutionsError, authLinkError, syncStatusError, isConnectedError: statusParam === 'disconnected' },
    mutations: {
      getAuthLink,
    },
  };
};

const authLinkFetcher = async (
  url: string,
  { arg }: { arg: { companyId: string; institutionId: string; userName: string; psuId?: string } },
) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Build query parameters
  const params = new URLSearchParams({
    companyId: arg.companyId,
    institutionId: arg.institutionId,
    userName: arg.userName,
  });

  // Add psuId if provided
  if (arg.psuId) {
    params.append('psuId', arg.psuId);
  }

  try {
    const response = await axios.get(`${url}?${params.toString()}`, options);

    return response.data;
  } catch (error) {
    // Parse Yapily error to extract user-friendly message
    const errorMessage = parseYapilyError(error);

    // If we got a parsed message, throw that; otherwise rethrow original error
    if (errorMessage) {
      throw new Error(errorMessage);
    }

    throw error;
  }
};
